import React, { FC, Fragment } from 'react';
import { Box, FormControl, Text } from '@bechtel/gds';
import { AttachmentMetadataInput, DeleteBlockAttachmentInput, DeleteBlockAttachmentPayload } from '@bechtel/types';
import { useMutation } from '@apollo/client';
import { useField } from 'formik';
import produce from 'immer';
import { get, map } from 'lodash';
import { FileUpload, FileUploadInput, FileUploadPreviewImg, FileUploadResponse } from '~/components/atoms';
import { DELETE_BLOCK_MEDIA } from '@bechtel/client-gql';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { MediaUploadProps } from './types';

const ErrorField = ({ error }) => {
  const { i18n } = useLingui();
  return (
    <Box pt={{ sm: '200' }}>
      <Text color="bechtel.red">{i18n._(error)}</Text>
    </Box>
  );
};

export const MediaUpload: FC<MediaUploadProps> = ({ fieldName }) => {
  const [field, meta, helpers] = useField(fieldName);
  const { setValue } = helpers;
  const [deleteMedia, { loading: deleteMediaLoading, data: deleteMediaData }] = useMutation<
    { deleteBlockAttachment: DeleteBlockAttachmentPayload },
    { input: DeleteBlockAttachmentInput }
  >(DELETE_BLOCK_MEDIA);
  const deleteMediaError = get(deleteMediaData, 'deleteBlockAttachment.error', null);
  return (
    <Box px={{ sm: '450' }} pt={{ sm: '500' }} pb={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
      <FormControl data-testid="weld-upload" label={t`Upload a Photo`}>
        <FileUpload
          {...field}
          filesCount={field?.value?.length ? field.value.length : 0}
          maxFiles={3}
          maxFileSizeMB={10}
          acceptFileTypes="image/jpeg, image/png"
          onSuccess={(res: FileUploadResponse, file) => {
            const newMediaData: AttachmentMetadataInput = {
              fileName: file.name,
              fileSize: file.size,
              attachmentId: res.attachmentId,
              mimeType: file.type,
            };
            const newValues = [...field.value, newMediaData];
            setValue(newValues);
          }}
        >
          {({ getInputProps, getRootProps, loading: mediaLoading, files, setFiles, acceptFileTypes }) => {
            return (
              <Fragment>
                <FileUploadInput
                  getInputProps={getInputProps}
                  getRootProps={getRootProps}
                  loading={mediaLoading || deleteMediaLoading}
                  acceptFileTypes={acceptFileTypes}
                />
                {deleteMediaError && (
                  <Box my={{ sm: '200' }}>
                    <Trans>There was an error when deleting a file.</Trans>
                  </Box>
                )}
                {map(field.value, (file, index: number) => {
                  return (
                    <Box mb={{ sm: '400' }} key={`${file.attachmentId}.${index}`} flexDirection={{ sm: 'column' }}>
                      <FileUploadPreviewImg
                        file={files[index]}
                        onDelete={async () => {
                          const { data } = await deleteMedia({
                            variables: {
                              input: {
                                attachmentId: files[index].attachmentId,
                              },
                            },
                          });
                          if (data && !data.deleteBlockAttachment.error) {
                            const newValues = [...field.value];
                            newValues.splice(index, 1);
                            setValue(newValues);
                            setFiles(
                              produce((draft) => {
                                //@ts-ignore splice will exist on draft since it is an array
                                draft.splice(index, 1);
                              })
                            );
                          }
                        }}
                      />
                    </Box>
                  );
                })}
              </Fragment>
            );
          }}
        </FileUpload>
      </FormControl>
      {meta.touched && meta.error ? <ErrorField error={meta.error} /> : null}
    </Box>
  );
};
